import React from 'react';
import { Check } from 'lucide-react';
import type {
    DocumentSchema,
    InputNode,
    SectionNode,
} from '../../documents/types';
import type { PolicyDocumentMeta } from './PolicyDocumentView';
import {
    isEmptyValue,
    formatDate,
    evaluateCondition,
    resolveFixedOptionLabel,
} from '../utils/documentRendererUtils';
import { formatMRIParameters } from '../../reports/cpt/utils/cptDocumentViewUtils';
import { renderConditionalsListValue } from '../../preview/conditionalsLists';
import {
    PreviewField,
    PreviewRichText,
    type PreviewFieldData,
    type PreviewHeaderConfig,
    type PreviewSectionData,
} from '@nordinsight/implant-core/ui';

type TFunc = (key: string, opts?: Record<string, any>) => string;

export function buildPolicyHeaderConfig(
    meta: PolicyDocumentMeta,
    t: TFunc
): PreviewHeaderConfig {
    const metaFields: PreviewFieldData[] = [];

    if (meta.effectiveDate) {
        metaFields.push({
            key: 'effective',
            label: t('departmentPolicies.preview.effective'),
            value: formatDate(meta.effectiveDate),
        });
    }
    if (meta.expiryDate) {
        metaFields.push({
            key: 'expires',
            label: t('departmentPolicies.preview.expires'),
            value: formatDate(meta.expiryDate),
        });
    }
    if (meta.authors?.length) {
        metaFields.push({
            key: 'authors',
            label: t('departmentPolicies.preview.authors'),
            value: meta.authors.join(', '),
        });
    }
    return {
        title: meta.title || t('departmentPolicies.preview.policyFallback'),
        meta: metaFields,
        generatedDate: new Date().toISOString().slice(0, 10),
        eyebrow: t('departmentPolicies.preview.eyebrow'),
        summaryLabel: t('departmentPolicies.preview.summaryLabel'),
        showBrand: false,
    };
}

function formatPreviewValue(
    field: InputNode,
    value: any
): React.ReactNode | string | null {
    if (isEmptyValue(value)) return null;

    if (
        (field.inputType === 'richtext' ||
            field.inputType === 'richtext-enhanced') &&
        typeof value === 'string'
    ) {
        return <PreviewRichText html={value} />;
    }

    if (field.inputType === 'date') return formatDate(String(value));

    if (field.inputType === 'select' || field.inputType === 'radio') {
        const label = resolveFixedOptionLabel(field, value);
        return typeof label === 'string' ? label : String(value);
    }

    if (field.inputType === 'tag-list' && Array.isArray(value)) {
        const fixed = field.data?.options?.fixed;
        return (
            <ul className="list-disc pl-5 space-y-0.5">
                {value.map((v: string) => {
                    const label =
                        fixed?.find((o: any) => o.value === v)?.label ?? v;
                    return <li key={v}>{label}</li>;
                })}
            </ul>
        );
    }

    if (Array.isArray(value)) {
        if (value.length > 0 && typeof value[0] !== 'object')
            return value.join(', ');
        return value
            .map((item) =>
                typeof item === 'object'
                    ? Object.values(item)
                          .filter((v) => !isEmptyValue(v))
                          .join(' — ')
                    : String(item)
            )
            .join('\n');
    }

    if (typeof value === 'boolean') return value ? 'Yes' : 'No';

    return String(value);
}

function buildConditionalsFields(
    condValue: any,
    t?: TFunc
): PreviewFieldData[] {
    const fields: PreviewFieldData[] = [];
    const formatted = formatMRIParameters(condValue);

    for (const [label, rawValue] of Object.entries(formatted)) {
        const listValue = renderConditionalsListValue(label, condValue, t);
        if (listValue) {
            fields.push({
                key: `cond-${label}`,
                label,
                value: listValue,
            });
            continue;
        }

        const value = rawValue as string;
        if (!value || value === '<p></p>') continue;

        const hasHtml = /<[a-z][\s\S]*>/i.test(value);
        fields.push({
            key: `cond-${label}`,
            label,
            value: hasHtml ? <PreviewRichText html={value} /> : value,
        });
    }

    return fields;
}

function RepeaterPreview({
    field,
    items,
    data,
}: {
    field: InputNode;
    items: any[];
    data: Record<string, any>;
}) {
    const children = (field.fields ?? []).filter(
        (c) => c.kind === 'input'
    ) as InputNode[];

    return (
        <div className="space-y-3">
            {items.map((item, index) => {
                const visibleChildren = children.filter((child) => {
                    if (
                        child.hidden &&
                        evaluateCondition(child.hidden, data, item)
                    )
                        return false;
                    return !isEmptyValue(item?.[child.id]);
                });
                if (!visibleChildren.length) return null;

                return (
                    <div key={`${field.id}-${index}`}>
                        {visibleChildren.map((child) => (
                            <PreviewField
                                key={child.id}
                                field={{
                                    key: child.id,
                                    label: child.title || '',
                                    value: formatPreviewValue(
                                        child,
                                        item[child.id]
                                    ),
                                }}
                            />
                        ))}
                    </div>
                );
            })}
        </div>
    );
}

export function buildPolicyPreviewSections({
    schema,
    data,
    t,
}: {
    schema: DocumentSchema;
    data: Record<string, any>;
    t?: TFunc;
}): PreviewSectionData[] {
    const sections: PreviewSectionData[] = [];
    const schemaSections = Array.isArray(schema.sections)
        ? schema.sections
        : [];

    const visibleSections = schemaSections.filter((section: SectionNode) => {
        if (section.sectionType === 'actions') return false;
        if (section.hidden && evaluateCondition(section.hidden, data))
            return false;
        return true;
    });

    for (const section of visibleSections) {
        const fields: PreviewFieldData[] = [];
        const custom: React.ReactNode[] = [];
        let hasConditionals = false;
        const sectionFields = Array.isArray(section.fields)
            ? section.fields
            : [];

        const plainInputCount = sectionFields.filter(
            (node) =>
                node.kind === 'input' &&
                node.inputType !== 'repeater' &&
                node.functionKey !== 'conditionals' &&
                !(node.hidden && evaluateCondition(node.hidden, data)) &&
                !isEmptyValue(data[node.id])
        ).length;

        for (const node of sectionFields) {
            if (node.hidden && evaluateCondition(node.hidden, data)) continue;

            if (node.kind === 'action') {
                if (node.actionType === 'checkbox' && data[node.id]) {
                    custom.push(
                        <div
                            key={node.id}
                            className="flex items-center gap-2 text-sm"
                        >
                            <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                            <span>{node.title}</span>
                        </div>
                    );
                }
                continue;
            }

            if (node.kind === 'view') {
                if (node.viewType !== 'total-summary') continue;
                const dependency = node.viewProps?.dependency || node.id;
                const items = Array.isArray(data[dependency])
                    ? data[dependency]
                    : [];
                const sumField = node.viewProps?.sumField;
                if (!items.length || !sumField) continue;
                const total = items.reduce(
                    (sum: number, item: any) =>
                        sum + (Number(item[sumField]) || 0),
                    0
                );
                fields.push({
                    key: node.id,
                    label: node.title || 'Total',
                    value: `${total}${node.viewProps?.unit ? ` ${node.viewProps.unit}` : ''}`,
                });
                continue;
            }

            if (node.kind !== 'input') continue;

            const value = data[node.id];

            if (node.functionKey === 'conditionals') {
                if (!value || Object.keys(value).length === 0) continue;
                const condFields = buildConditionalsFields(value, t);
                if (condFields.length) {
                    hasConditionals = true;
                    fields.push(...condFields);
                }
                continue;
            }

            if (node.inputType === 'repeater') {
                const items = Array.isArray(value) ? value : [];
                if (!items.length) continue;
                custom.push(
                    <div key={node.id} className="space-y-2">
                        {node.title && (
                            <h4 className="text-sm font-semibold">
                                {node.title}
                            </h4>
                        )}
                        <RepeaterPreview
                            field={node}
                            items={items}
                            data={data}
                        />
                    </div>
                );
                continue;
            }

            if (isEmptyValue(value)) continue;

            if (
                (node.inputType === 'richtext' ||
                    node.inputType === 'richtext-enhanced') &&
                typeof value === 'string'
            ) {
                custom.push(
                    <div key={node.id} className="space-y-1">
                        {plainInputCount > 1 && node.title && (
                            <h4 className="text-sm font-semibold">
                                {node.title}
                            </h4>
                        )}
                        <PreviewRichText html={value} />
                    </div>
                );
                continue;
            }

            fields.push({
                key: node.id,
                label: node.title || '',
                value: formatPreviewValue(node, value),
            });
        }

        if (!fields.length && !custom.length) continue;

        sections.push({
            key: section.id,
            title: section.title || '',
            fields: fields.length ? fields : undefined,
            divided: hasConditionals,
            customContent: custom.length ? (
                <div className="space-y-3">{custom}</div>
            ) : undefined,
        });
    }

    return sections;
}
